/**
 * Partner tag taxonomy (#72180 + #72193 dialog) — hybrid systémové + volné tagy.
 *
 * `app.partners.tags` je `text[]` (viz `db/schema.ts:807`) bez DB enum
 * constraintu — sloupec drží **dva druhy** tagů sjednocené v jednom poli:
 *
 *  1. **Systémové tagy** s prefixem `system:` — uzavřená sada s pevně
 *     definovaným UI metadatem (label, barva, ikona). Drží role partnera
 *     v adresáři: `system:customer` (objednává u nás) a `system:partner`
 *     (subdodavatel). Filter UI je vystavuje jako 2 toggle chipy nad
 *     tabulkou. Filip je nemá ručně přepisovat — když potřebuje změnu,
 *     edituje přes detail modal (PR-A detach flow respektován).
 *
 *  2. **Custom tagy** = volný text bez prefixu (`VIP`, `Strategický`,
 *     `Reklamace`, ...). Filter UI je vystavuje jako dropdown s
 *     checkboxem (#72180 plán — viz `tag-filter-dropdown.tsx`). DB to
 *     necheck-uje — pravopisné varianty jsou Filipova odpovědnost,
 *     dropdown ukazuje `DISTINCT tags` z `/api/v1/partners/tags` aby
 *     se předešlo zbytečným duplikátům.
 *
 * Prefix `system:` je rezervovaný namespace — žádný custom tag nesmí
 * začínat tímto stringem. Helper `isSystemTag()` to kontroluje na FE i
 * BE konzistentně; create/PATCH endpoint by mohl v budoucnu odmítnout
 * neznámé `system:*` tagy (zatím tolerujeme — Filip žádné neuvidí
 * v dropdownu, takže to nehrozí).
 *
 * **Proč sloučené v `tags[]` (a ne 2 oddělené sloupce)?**
 * Filip 11.6. řekl „chci hybrid systémové + volné tagy v jednom poli"
 * (Q3 plánu). Důvod: filter UX je konzistentní (chipy se chovají stejně
 * bez ohledu na původ), DB schema zůstává minimální (jeden text[]
 * sloupec s GIN indexem v migraci 0011). Cena: helper musí rozlišit
 * system vs custom — proto `splitTags()`.
 */

/**
 * Uzavřená sada systémových tagů. Prefix `system:` je rezervovaný —
 * žádný custom tag nesmí kolidovat. Hodnoty jsou stabilní (změna by
 * vyžadovala data migraci na produkčním DB), labels mohou být lokalizované.
 */
export const SYSTEM_TAG_CUSTOMER = 'system:customer' as const;
export const SYSTEM_TAG_PARTNER = 'system:partner' as const;

export type SystemTag = typeof SYSTEM_TAG_CUSTOMER | typeof SYSTEM_TAG_PARTNER;

/**
 * UI metadata pro systémové tagy. Sdílené BE + FE — BE ho používá v
 * `GET /api/v1/partners/tags` response (přiloží label/color/icon),
 * FE ho čte v `<TagChip>` a filter chip toggle.
 *
 * Barvy mapují na TailAdmin Badge color enum (`color: 'primary' | 'success'
 * | 'warning' | 'info' | 'light'`). Ikony jsou string klíče do FE
 * vendored TailAdmin icon setu (rendrují se přes lookup v `<TagChip>`).
 */
export interface SystemTagMetadata {
  /** Pevná hodnota v `tags[]` (s prefixem). */
  readonly value: SystemTag;
  /** Lokalizovaný label pro UI (CS). */
  readonly label: string;
  /** TailAdmin Badge color. */
  readonly color: 'primary' | 'success' | 'warning' | 'info' | 'light';
  /** Stručný popis pro tooltip / dropdown hint. */
  readonly description: string;
}

export const SYSTEM_TAGS: Readonly<Record<SystemTag, SystemTagMetadata>> = {
  [SYSTEM_TAG_CUSTOMER]: {
    value: SYSTEM_TAG_CUSTOMER,
    label: 'Zákazník',
    color: 'info',
    description: 'Kontakt který u nás objednává zakázky.',
  },
  [SYSTEM_TAG_PARTNER]: {
    value: SYSTEM_TAG_PARTNER,
    label: 'Partner',
    color: 'success',
    description: 'Subdodavatel — zpracovává poptávky / nabídky pro nás.',
  },
};

/**
 * Seznam systémových tagů v UI priority pořadí (pro chip rendering
 * v listu — `system:customer` první, `system:partner` druhý, custom
 * tagy abecedně za nimi).
 */
export const SYSTEM_TAG_ORDER: readonly SystemTag[] = [SYSTEM_TAG_CUSTOMER, SYSTEM_TAG_PARTNER];

/**
 * TRUE pokud tag patří do systémové sady (prefix `system:`). Nepoužívá
 * konkrétní whitelist hodnot — toleruje budoucí `system:*` rozšíření
 * bez nutnosti rebuild shared package všude.
 */
export function isSystemTag(tag: string): tag is SystemTag {
  return tag.startsWith('system:');
}

/**
 * Rozdělí pole tagů na systémové + custom. Pořadí v `system` odpovídá
 * `SYSTEM_TAG_ORDER` (zákazník → partner); custom tagy abecedně.
 *
 * Tolerantní k duplikátům (dedupe přes Set) a k null inputu (vrátí
 * prázdné pole na obou stranách). Spotřebitelé můžou předat
 * `partner.tags` přímo bez null guardu.
 */
export function splitTags(tags: readonly string[] | null | undefined): {
  system: SystemTag[];
  custom: string[];
} {
  if (!tags || tags.length === 0) return { system: [], custom: [] };

  const seen = new Set<string>();
  const systemSet = new Set<SystemTag>();
  const customList: string[] = [];

  for (const tag of tags) {
    if (seen.has(tag)) continue;
    seen.add(tag);
    if (isSystemTag(tag)) {
      // Whitelist check — neznámé `system:*` tagy přeskočíme (defensivní,
      // ať se v UI neobjeví rozbité chipy bez metadata).
      if (tag === SYSTEM_TAG_CUSTOMER || tag === SYSTEM_TAG_PARTNER) {
        systemSet.add(tag);
      }
    } else {
      customList.push(tag);
    }
  }

  // System tagy v pevném pořadí (customer před partner), custom abecedně.
  const system = SYSTEM_TAG_ORDER.filter((t) => systemSet.has(t));
  const custom = customList.sort((a, b) => a.localeCompare(b, 'cs'));

  return { system, custom };
}

/**
 * Lookup metadata pro daný system tag. Vrátí `null` pokud tag není ve
 * známé sadě (defensive — caller by měl render fallback).
 */
export function getSystemTagMetadata(tag: string): SystemTagMetadata | null {
  if (tag === SYSTEM_TAG_CUSTOMER || tag === SYSTEM_TAG_PARTNER) {
    return SYSTEM_TAGS[tag];
  }
  return null;
}
